import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { FileText, Save, History, RotateCcw, Loader2, CheckCircle, Clock, User } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const PAGES = [
  { key: 'agb', label: 'AGB', icon: FileText },
  { key: 'datenschutz', label: 'Datenschutz', icon: FileText },
  { key: 'impressum', label: 'Impressum', icon: FileText },
];

const AdminLegal = () => {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState('agb');
  const [contents, setContents] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(null);
  const [versions, setVersions] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [rollingBack, setRollingBack] = useState(null);

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchPage = useCallback(async (pageType) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/legal/${pageType}`, { headers });
      if (res.ok) {
        const data = await res.json();
        setContents(prev => ({ ...prev, [pageType]: data.content || '' }));
      }
    } catch (_) {}
  }, [token]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await Promise.all(PAGES.map(p => fetchPage(p.key)));
      setLoading(false);
    };
    load();
  }, [fetchPage]);

  const savePage = async (pageType) => {
    setSaving(true);
    setSaved(null);
    try {
      const res = await fetch(`${API_URL}/api/admin/legal/${pageType}`, {
        method: 'PUT', headers,
        body: JSON.stringify({ content: contents[pageType] || '' }),
      });
      if (res.ok) {
        const data = await res.json();
        setSaved(data.message);
        setTimeout(() => setSaved(null), 4000);
      } else {
        const err = await res.json();
        setSaved(`Fehler: ${err.detail}`);
      }
    } catch (_) {
      setSaved('Netzwerkfehler');
    }
    setSaving(false);
  };

  const fetchVersions = async (pageType) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/legal/${pageType}/versions`, { headers });
      if (res.ok) setVersions(await res.json());
    } catch (_) {}
    setShowHistory(true);
  };

  const rollback = async (pageType, versionId) => {
    setRollingBack(versionId);
    try {
      const res = await fetch(`${API_URL}/api/admin/legal/${pageType}/rollback/${versionId}`, {
        method: 'POST', headers,
      });
      if (res.ok) {
        await fetchPage(pageType);
        setSaved('Rollback erfolgreich');
        setShowHistory(false);
        setTimeout(() => setSaved(null), 4000);
      }
    } catch (_) {}
    setRollingBack(null);
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-[400px]"><Loader2 className="w-8 h-8 animate-spin text-[hsl(var(--primary))]" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="admin-legal">
      {saved && (
        <Alert className="bg-[hsl(var(--chart-2)/0.1)] border-[hsl(var(--chart-2))]">
          <CheckCircle className="h-4 w-4 text-[hsl(var(--chart-2))]" />
          <AlertDescription className="text-[hsl(var(--chart-2))]">{saved}</AlertDescription>
        </Alert>
      )}

      {/* Legal Pages Editor */}
      <Card className="card-shadow">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <FileText className="w-5 h-5 text-[hsl(var(--primary))]" />
            Rechtliche Seiten bearbeiten
          </CardTitle>
          <CardDescription>AGB, Datenschutzerklärung und Impressum bearbeiten. Jede Änderung wird versioniert.</CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-3" data-testid="legal-tabs">
              {PAGES.map(p => (
                <TabsTrigger key={p.key} value={p.key} data-testid={`legal-tab-${p.key}`}>
                  {p.label}
                </TabsTrigger>
              ))}
            </TabsList>

            {PAGES.map(p => (
              <TabsContent key={p.key} value={p.key} className="mt-4">
                <div className="space-y-4">
                  <textarea
                    className="w-full min-h-[400px] p-4 border border-[hsl(var(--border))] rounded-lg font-mono text-sm resize-y focus:ring-2 focus:ring-[hsl(var(--primary)/0.2)] focus:border-[hsl(var(--primary))] outline-none"
                    value={contents[p.key] || ''}
                    onChange={(e) => setContents(prev => ({ ...prev, [p.key]: e.target.value }))}
                    placeholder={`${p.label} Inhalt hier eingeben (HTML erlaubt)...`}
                    data-testid={`legal-editor-${p.key}`}
                  />
                  <div className="flex items-center justify-between">
                    <Button variant="outline" onClick={() => fetchVersions(p.key)} data-testid={`legal-history-${p.key}`}>
                      <History className="w-4 h-4 mr-2" />
                      Versionshistorie
                    </Button>
                    <Button onClick={() => savePage(p.key)} disabled={saving} data-testid={`legal-save-${p.key}`}>
                      {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                      {p.label} speichern
                    </Button>
                  </div>
                </div>
              </TabsContent>
            ))}
          </Tabs>
        </CardContent>
      </Card>

      {/* Version History Dialog */}
      <Dialog open={showHistory} onOpenChange={setShowHistory}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5" />
              Versionshistorie — {PAGES.find(p => p.key === activeTab)?.label}
            </DialogTitle>
            <DialogDescription>
              Alle Änderungen mit Timestamp und Admin. Klicken Sie "Wiederherstellen" um auf eine Version zurückzusetzen.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-4">
            {versions.length === 0 ? (
              <p className="text-center text-[hsl(var(--muted-foreground))] py-8">Keine Versionen vorhanden</p>
            ) : (
              versions.map((v) => (
                <div key={v.id} className={`p-4 rounded-lg border ${v.is_rollback ? 'bg-amber-50 border-amber-200' : 'bg-[hsl(var(--surface-2))] border-[hsl(var(--border))]'}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-[hsl(var(--primary)/0.1)] flex items-center justify-center text-xs font-bold text-[hsl(var(--primary))]">
                        v{v.version_number}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <User className="w-3.5 h-3.5 text-[hsl(var(--muted-foreground))]" />
                          <span className="text-sm font-medium">{v.admin_name}</span>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-[hsl(var(--muted-foreground))]">
                          <Clock className="w-3 h-3" />
                          {new Date(v.created_at).toLocaleString('de-CH')}
                          {v.is_rollback && <span className="text-amber-600 font-medium">Rollback von v{v.rollback_from_version}</span>}
                        </div>
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => rollback(activeTab, v.id)}
                      disabled={rollingBack === v.id}
                      data-testid={`rollback-${v.id}`}
                    >
                      {rollingBack === v.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5 mr-1.5" />}
                      Wiederherstellen
                    </Button>
                  </div>
                  <details className="mt-2">
                    <summary className="text-xs text-[hsl(var(--muted-foreground))] cursor-pointer hover:text-[hsl(var(--foreground))]">
                      Inhalt anzeigen ({v.content?.length || 0} Zeichen)
                    </summary>
                    <pre className="mt-2 p-3 bg-white rounded border text-xs overflow-x-auto max-h-40 overflow-y-auto">{v.content?.slice(0, 2000)}{v.content?.length > 2000 ? '...' : ''}</pre>
                  </details>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminLegal;
